import React, { useEffect, useRef } from 'react';
import {
    CarouselContainer,
    CarouselSlide,
    CarouselViewport,
    CarouselWrapper,
    SlideGrid,
} from '@components/carousel/styled.ts';
import { TEST_IDS } from '@constants/testIds.ts';
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel from 'embla-carousel-react';
import { chunk } from 'lodash';

interface CarouselProps {
    items: any[];
    renderItem: (item: any) => React.ReactNode;
    gridItemsSize: number;
    columnsPerRow: number;
    options?: EmblaOptionsType;
    onInit?: (emblaApi: EmblaCarouselType) => void;
    onSelect?: (selectedIndex: number) => void;
    slideWidth?: string;
    pageHeight: string;
    loop?: boolean;
    grid?: boolean;
}

const defaultOptions: EmblaOptionsType = {
    duration: 30,
    slidesToScroll: 1,
    watchDrag: true,
};

export const BaseCarousel: React.FC<CarouselProps> = ({
    items,
    renderItem,
    options,
    onInit,
    onSelect,
    gridItemsSize,
    columnsPerRow,
    pageHeight = '100%',
    slideWidth = '100%',
    loop = false,
    grid = false,
}) => {
    const emblaOptions = {
        ...(options || defaultOptions),
    };

    const plugins = loop ? [AutoScroll({ speed: 10, startDelay: 100 })] : [];
    const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);
    const viewportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!emblaApi) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                emblaApi.scrollPrev();
            }
            if (e.key === 'ArrowRight') {
                emblaApi.scrollNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;

        onInit?.(emblaApi);

        const handleSelect = () => {
            const index = emblaApi.selectedScrollSnap();
            onSelect?.(index);
        };

        emblaApi.on('select', handleSelect);
        handleSelect();

        return () => {
            emblaApi.off('select', handleSelect);
        };
    }, [emblaApi, onInit, onSelect]);

    const gridSlides = chunk(items, gridItemsSize);

    return (
        <CarouselWrapper $pageHeight={pageHeight}>
            <CarouselViewport
                ref={(el) => {
                    emblaRef(el);
                    viewportRef.current = el;
                }}
            >
                <CarouselContainer data-cy={TEST_IDS.SHARED.CAROUSEL_CONTAINER}>
                    {grid
                        ? gridSlides.map((gridSlide, gridSlideIndex) => (
                              <CarouselSlide
                                  key={gridSlide[0]?.id ?? `group-${gridSlideIndex}`}
                                  width={slideWidth}
                                  data-cy={TEST_IDS.SHARED.CAROUSEL_SLIDE}
                              >
                                  <SlideGrid $columns={columnsPerRow} data-cy={TEST_IDS.SHARED.CAROUSEL_GRID}>
                                      {gridSlide.map((item, itemIndex) => (
                                          <div key={item.id ?? `${gridSlideIndex}-${itemIndex}`}>
                                              {renderItem(item)}
                                          </div>
                                      ))}
                                  </SlideGrid>
                              </CarouselSlide>
                          ))
                        : items.map((item, i) => (
                              <CarouselSlide
                                  key={item.id ?? i}
                                  width={slideWidth}
                                  data-cy={TEST_IDS.SHARED.CAROUSEL_SLIDE}
                              >
                                  {renderItem(item)}
                              </CarouselSlide>
                          ))}
                </CarouselContainer>
            </CarouselViewport>
        </CarouselWrapper>
    );
};
